import { useState, useSyncExternalStore, type ReactNode } from "react";
import { AppIcon } from "./AppIcon.js";
import { SignInButton, signInErrorText, useSignIn } from "./SignInWithOpenMarkets.js";
import { clearUpgradeReturn, upgradeFor, useAccess, useUpgradeReturn } from "../lib/omauth.js";
import { entitlementOf, type EntitlementInfo } from "../lib/api.js";
import { recordTried } from "../lib/desk.js";

/**
 * Access gates. Signed-out visitors get the public Markets + Contest views; everything else
 * shows one of two cards in the app's own visual language:
 *
 *   LockedCard   "Sign in to see this"    — no credential
 *   UnlockCard   "Unlock <feature>"       — signed in, but the plan or the grant lacks it
 *
 * RequireAccess wraps a whole route; the cards are also used inline (ErrorState renders them
 * for `sign_in_required` / `entitlement_required` / `scope_not_granted` answers).
 */

/** Plain-words names for scope codes (shown on Unlock cards). */
const SCOPE_LABEL: Record<string, string> = {
  "account:read": "your account (positions, orders, balances)",
  "trade:execute": "order placement",
  "depth:read": "order-book depth",
  "pricing:history": "price history",
  "gamestate:read": "live game state",
  "stream:realtime": "real-time streaming",
  "signals:read": "signals",
  "arbitrage:read": "arbitrage",
};
export function scopeLabel(code: string): string {
  return SCOPE_LABEL[code] ?? code;
}

function Shell({ compact, children }: { compact?: boolean; children: ReactNode }) {
  if (compact) {
    return <div className="m-3 rounded-lg border border-border bg-panel-2 p-4">{children}</div>;
  }
  return (
    <div className="flex justify-center p-4 sm:p-8">
      <div className="w-full max-w-md rounded-lg border border-border bg-panel-2 p-6 shadow-lg">{children}</div>
    </div>
  );
}

/** "Sign in to see this" — the signed-out card. */
export function LockedCard({
  title = "Sign in to see this",
  body,
  compact,
}: {
  title?: string;
  body?: ReactNode;
  compact?: boolean;
}) {
  return (
    <Shell compact={compact}>
      {!compact && (
        <div className="mb-4 flex items-center gap-2">
          <AppIcon size={28} />
          <span className="text-base font-semibold tracking-tight">greenbook</span>
        </div>
      )}
      <h2 className={compact ? "text-sm font-semibold text-ink" : "text-lg font-semibold text-ink"}>{title}</h2>
      <p className="mt-1 text-[13px] leading-relaxed text-dim">
        {body ??
          "Connect the OpenMarkets account you already have — free works. You approve exactly what greenbook can do; no password is handled here."}
      </p>
      <div className="mt-4">
        <SignInButton label="Sign in with OpenMarkets" size="lg" feedback />
      </div>
    </Shell>
  );
}

/**
 * "Unlock <feature>" — signed in, but the plan lacks it (`entitlement_required`, carries an
 * upgrade offer) or the grant does (`scope_not_granted` / the session never received the
 * scope: the user unticked an optional scope). The button runs the documented flow: upgrade
 * page (if any) → re-consent.
 */
export function UnlockCard({
  feature,
  scope,
  entitlement,
  compact,
}: {
  feature: string;
  scope?: string;
  entitlement?: EntitlementInfo | null;
  compact?: boolean;
}) {
  const { error: signInError } = useSignIn();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const up = entitlement?.upgrade ?? null;
  const declined = entitlement?.code === "scope_not_granted" || (!entitlement && !!scope);
  const missing = entitlement?.missing.length ? entitlement.missing : scope ? [scope] : [];

  const go = async () => {
    setBusy(true);
    setNote(null);
    try {
      const target = entitlement
        ? { code: entitlement.code, details: { missing: entitlement.missing, upgrade: entitlement.upgrade ?? undefined } }
        : missing;
      const r = await upgradeFor(target);
      if (r.upgrade?.status === "canceled") setNote("No change — you can unlock it any time.");
      else if (r.upgrade?.status === "closed") setNote("The upgrade window closed. If you finished, click Re-authorize.");
      else if (r.consent?.status === "denied") setNote("Re-authorization cancelled.");
      else if (r.consent?.status === "error") setNote(signInErrorText(r.consent.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell compact={compact}>
      <div className="mb-1 flex items-center gap-2">
        <span className="rounded bg-amber/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber">
          {declined && !up ? "not granted" : "locked"}
        </span>
        {up?.price_label && <span className="nums text-[11px] text-dim">{up.price_label}</span>}
      </div>
      <h2 className={compact ? "text-sm font-semibold text-ink" : "text-lg font-semibold text-ink"}>
        Unlock {up?.label ?? feature}
      </h2>
      <p className="mt-1 text-[13px] leading-relaxed text-dim">
        {declined && !up
          ? `greenbook wasn’t granted ${missing.map(scopeLabel).join(", ") || "this"}. Re-authorize to approve it — you choose again on the consent screen.`
          : `Your OpenMarkets plan doesn’t include ${missing.map(scopeLabel).join(", ") || "this"}. Unlock it on OpenMarkets (you’re charged by OpenMarkets, not greenbook), then approve the new access.`}
      </p>
      <button
        onClick={() => void go()}
        disabled={busy}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-up/20 px-3 py-2.5 text-[14px] font-semibold text-up hover:bg-up/30 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy ? "Waiting for OpenMarkets…" : declined && !up ? "Re-authorize greenbook" : "Unlock"}
      </button>
      {note && <p className="mt-2 text-[12px] text-dim">{note}</p>}
      {!note && signInError && <p className="mt-2 text-[12px] text-down">{signInErrorText(signInError)}</p>}
    </Shell>
  );
}

/* ------------------------------------------------------- unlock explainer */

/**
 * What greenbook says BEFORE handing someone to the OpenMarkets upgrade page: why the feature
 * needs it, what happens next, and who bills them. Scopes without an entry get a generic one.
 */
const EXPLAINERS: Record<string, { title: string; points: string[]; steps: string[] }> = {
  "trade:execute": {
    title: "Trade with real money",
    points: [
      "Paper mode is free: practice on every venue with play money, no limits.",
      "Real mode sends your orders to the sportsbooks and exchanges you’ve connected on OpenMarkets, using your own balances there.",
      "Placing real orders needs real-money trading on your OpenMarkets plan. OpenMarkets handles the upgrade and billing — greenbook never sees your payment details.",
    ],
    steps: [
      "Choose a plan on OpenMarkets — you’ll see the price before you pay.",
      "Approve greenbook to place orders for you.",
      "Connect and fund a venue on OpenMarkets, then flip to Real.",
    ],
  },
};

function explainerFor(scope: string) {
  return (
    EXPLAINERS[scope] ?? {
      title: `Unlock ${scopeLabel(scope)}`,
      points: [
        `greenbook needs ${scopeLabel(scope)} for this, and your OpenMarkets plan doesn’t include it yet.`,
        "OpenMarkets handles the upgrade and billing — greenbook never sees your payment details.",
      ],
      steps: ["Choose a plan on OpenMarkets — you’ll see the price before you pay.", "Approve the new access for greenbook."],
    }
  );
}

let unlockScope: string | null = null;
const unlockListeners = new Set<() => void>();
/** Open the unlock explainer for `scope` (the upgrade itself starts from its Continue button). */
export function showUnlock(scope: string): void {
  // Remember the feature was tried (per browser): the Desk's upgrade nudges put these first.
  recordTried(scope);
  unlockScope = scope;
  for (const l of unlockListeners) l();
}
function hideUnlock(): void {
  unlockScope = null;
  for (const l of unlockListeners) l();
}
function useUnlockScope(): string | null {
  return useSyncExternalStore(
    (cb) => {
      unlockListeners.add(cb);
      return () => unlockListeners.delete(cb);
    },
    () => unlockScope,
  );
}

/** Mounted once in App. Continue runs the documented flow: upgrade page → re-consent. */
export function UnlockExplainer() {
  const scope = useUnlockScope();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  if (!scope) return null;
  const ex = explainerFor(scope);
  const close = () => {
    if (busy) return;
    setNote(null);
    hideUnlock();
  };
  const go = async () => {
    setBusy(true);
    setNote(null);
    try {
      const r = await upgradeFor([scope]);
      if (r.upgrade?.status === "canceled") setNote("No change — you can unlock it any time.");
      else if (r.upgrade?.status === "closed") setNote("The OpenMarkets window closed. If you finished, click Continue again to approve.");
      else if (r.consent?.status === "denied") setNote("Approval cancelled.");
      else if (r.consent?.status === "error") setNote(signInErrorText(r.consent.message));
      else if (r.consent?.status === "success") {
        setBusy(false);
        hideUnlock();
        return;
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={close}>
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md rounded-lg border border-border bg-panel-2 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-ink">{ex.title}</h2>
          <button onClick={close} className="text-faint hover:text-ink" title="Close">
            ✕
          </button>
        </div>
        <ul className="space-y-2 text-[13px] leading-relaxed text-dim">
          {ex.points.map((p) => (
            <li key={p} className="flex gap-2">
              <span className="text-faint">•</span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-faint">What happens next</p>
        <ol className="mt-2 space-y-1.5 text-[13px] text-ink">
          {ex.steps.map((st, i) => (
            <li key={st} className="flex gap-2">
              <span className="nums text-faint">{i + 1}.</span>
              <span>{st}</span>
            </li>
          ))}
        </ol>
        <div className="mt-5 flex gap-2">
          <button
            onClick={close}
            disabled={busy}
            className="min-h-11 flex-1 rounded-md border border-border-2 text-[13px] font-semibold text-dim hover:bg-raised disabled:opacity-60"
          >
            Not now
          </button>
          <button
            onClick={() => void go()}
            disabled={busy}
            className="min-h-11 flex-[2] rounded-md bg-up/20 text-[13px] font-semibold text-up hover:bg-up/30 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? "Waiting for OpenMarkets…" : "Continue to OpenMarkets"}
          </button>
        </div>
        {note && <p className="mt-2 text-[12px] text-dim">{note}</p>}
      </div>
    </div>
  );
}

/**
 * Route-level gate. Signed out → LockedCard; signed in without `scope` → UnlockCard; else the
 * page. `scope` omitted = any signed-in credential (the API's own 403 then drives Unlock, via
 * ErrorState).
 */
export function RequireAccess({
  feature,
  scope,
  signedOutBody,
  children,
}: {
  feature: string;
  scope?: string;
  signedOutBody?: ReactNode;
  children: ReactNode;
}) {
  const access = useAccess(scope);
  if (access.ok) return <>{children}</>;
  if (access.reason === "signed_out") return <LockedCard title={`Sign in to see ${feature}`} body={signedOutBody} />;
  return <UnlockCard feature={feature} scope={access.scope} />;
}

/** Inline gate for an error a query returned; null when it isn't an access problem. */
export function AccessErrorCard({ error, feature = "this", compact = true }: { error: unknown; feature?: string; compact?: boolean }) {
  const access = useAccess();
  const ent = entitlementOf(error);
  const code = error && typeof error === "object" && "code" in error ? String((error as { code: unknown }).code) : "";
  if (code === "sign_in_required" || code === "public_unavailable" || code === "no_credential") {
    return <LockedCard compact={compact} />;
  }
  if (ent) {
    // A public-feed 403 (signed out) can't be upgraded by the visitor — they need their own account.
    if (!access.ok) return <LockedCard compact={compact} />;
    return <UnlockCard feature={feature} entitlement={ent} compact={compact} />;
  }
  return null;
}

/** Small one-line lock for tight spots (the depth ladder, chart header). */
export function LockedInline({ scope }: { scope?: string }) {
  const access = useAccess(scope);
  if (access.ok) return null;
  return (
    <div className="px-3 py-3 text-center text-[11px] text-faint">
      {access.reason === "signed_out" ? (
        <>
          <span className="mr-2">Sign in to see {scope ? scopeLabel(scope) : "this"}.</span>
          <SignInButton size="sm" />
        </>
      ) : (
        <span>Unlock {scopeLabel(access.scope)} to see this.</span>
      )}
    </div>
  );
}

/**
 * After a redirect-mode upgrade the page reloads with `?om_upgrade=done`; the re-consent the
 * docs require must come from a click, so offer it here.
 */
export function UpgradeReturnBanner() {
  const ret = useUpgradeReturn();
  const access = useAccess();
  if (ret !== "done" || !access.ok) return null;
  return (
    <div className="fixed inset-x-3 bottom-3 z-[90] mx-auto flex max-w-md items-center gap-3 rounded-lg border border-up/40 bg-panel-2 px-4 py-3 shadow-xl">
      <div className="min-w-0 flex-1 text-[12px] text-ink">
        Upgrade complete. Approve the new access to finish.
      </div>
      <span onClick={() => clearUpgradeReturn()}>
        <SignInButton label="Approve" size="sm" opts={{ prompt: "consent" }} />
      </span>
      <button onClick={() => clearUpgradeReturn()} className="text-faint hover:text-ink" aria-label="dismiss">
        ✕
      </button>
    </div>
  );
}
